import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Assessment as AnalyticsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '../contexts/SnackbarContext';
import axios from 'axios';

interface Form {
  id: string;
  title: string;
  description: string;
  fields: any[];
  isActive: boolean;
  customUrl?: string;
  submissionCount: number;
  settings?: {
    requirePayment?: boolean;
    paymentAmount?: number;
  };
  createdAt: string;
  updatedAt: string;
  creator?: {
    username: string;
    email: string;
  };
}

const FormList: React.FC = () => {
  const [forms, setForms] = useState<Form[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; formId: string | null }>({
    open: false,
    formId: null
  });
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const fetchForms = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/forms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Forms API response:', response.data); // Debug log
      
      // The backend now returns { forms: [...], totalPages, currentPage, total }
      const formsData = response.data.forms || response.data || [];
      
      // Ensure formsData is an array
      if (Array.isArray(formsData)) {
        setForms(formsData);
      } else {
        console.error('Forms data is not an array:', formsData);
        setForms([]);
        showSnackbar('Invalid forms data received', 'error');
      }
    } catch (error) {
      console.error('Error fetching forms:', error);
      showSnackbar('Failed to fetch forms', 'error');
      setForms([]); // Ensure forms is always an array
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  const handleDeleteForm = async () => {
    if (!deleteDialog.formId) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/forms/${deleteDialog.formId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setForms(forms.filter(form => form.id !== deleteDialog.formId));
      showSnackbar('Form deleted successfully', 'success');
      setDeleteDialog({ open: false, formId: null });
    } catch (error) {
      console.error('Error deleting form:', error);
      showSnackbar('Failed to delete form', 'error');
    }
  };

  const handleTogglePublish = async (formId: string, currentStatus: boolean) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/forms/${formId}`,
        { isActive: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setForms(forms.map(form => 
        form.id === formId ? { ...form, isActive: !currentStatus } : form
      ));
      showSnackbar(
        `Form ${!currentStatus ? 'activated' : 'deactivated'} successfully`,
        'success'
      );
    } catch (error) {
      console.error('Error updating form:', error);
      showSnackbar('Failed to update form', 'error');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Forms</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/forms/new')}
        >
          Create New Form
        </Button>
      </Box>

      {forms.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No forms created yet
              </Typography>
              <Typography variant="body2" color="textSecondary" mb={2}>
                Create your first form to start collecting submissions
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/admin/forms/new')}
              >
                Create Your First Form
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(350px, 1fr))" gap={3}>
          {forms.map((form) => (
            <Card key={form.id}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography variant="h6" component="h2">
                      {form.title}
                    </Typography>
                    <Chip
                      label={form.isActive ? 'Active' : 'Inactive'}
                      color={form.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                  
                  <Typography variant="body2" color="textSecondary" mb={2}>
                    {form.description}
                  </Typography>

                  {form.customUrl && (
                    <Typography variant="caption" color="primary" sx={{ 
                      display: 'block', 
                      mb: 1, 
                      fontFamily: 'monospace',
                      bgcolor: 'primary.light',
                      color: 'primary.contrastText',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      width: 'fit-content'
                    }}>
                      /{form.customUrl}
                    </Typography>
                  )}

                  <Box display="flex" gap={2} mb={2}>
                    <Box textAlign="center">
                      <Typography variant="body2" color="textSecondary">Submissions</Typography>
                      <Typography variant="h6">{form.submissionCount || 0}</Typography>
                    </Box>
                    <Box textAlign="center">
                      <Typography variant="body2" color="textSecondary">Fields</Typography>
                      <Typography variant="h6">{form.fields?.length || 0}</Typography>
                    </Box>
                    <Box textAlign="center">
                      <Typography variant="body2" color="textSecondary">Payment</Typography>
                      <Typography variant="h6">{form.settings?.requirePayment ? 'Yes' : 'No'}</Typography>
                    </Box>
                  </Box>

                  <Typography variant="caption" color="textSecondary">
                    Created: {new Date(form.createdAt).toLocaleDateString()}
                  </Typography>
                </CardContent>

                <CardActions>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/admin/forms/${form.id}/view`)}
                    title="View Form"
                  >
                    <ViewIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/admin/forms/${form.id}/edit`)}
                    title="Edit Form"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/admin/submissions?formId=${form.id}`)}
                    title="View Submissions"
                  >
                    <AnalyticsIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setDeleteDialog({ open: true, formId: form.id })}
                    title="Delete Form"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                  <Button
                    size="small"
                    onClick={() => handleTogglePublish(form.id, form.isActive)}
                  >
                    {form.isActive ? 'Deactivate' : 'Activate'}
                  </Button>
                </CardActions>
              </Card>
          ))}
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, formId: null })}
      >
        <DialogTitle>Delete Form</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this form? This action cannot be undone.
            All submissions for this form will also be deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, formId: null })}>
            Cancel
          </Button>
          <Button onClick={handleDeleteForm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FormList;
